import {
  COVERAGE_LABEL,
  BLOCKER_LABEL,
  STATUS_LABEL,
  type CoverageBand,
  type BlockerBand,
  type StatusBand,
} from "@shared/trustLabels";
import type { RouterOutputs } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { TrustNote } from "./TrustNote";

/* Arabic display labels for the server-provided bands. Display-only — the
   band ENUM values are never touched; English defaults stay byte-identical. */
const STATUS_LABEL_AR: Record<StatusBand, string> = {
  on_track: "على المسار",
  mixed: "متفاوت",
  needs_attention: "يحتاج انتباهاً",
};
const BLOCKER_LABEL_AR: Record<BlockerBand, string> = {
  none: "لا شيء",
  some: "بعضها",
  elevated: "مرتفعة",
};
const COVERAGE_LABEL_AR: Record<CoverageBand, string> = {
  good: "تغطية جيدة",
  partial: "تغطية جزئية",
  limited: "تغطية محدودة",
};

/**
 * Team Pulse — renders ONLY the server-provided bands as labels. Computes no
 * band, no count, no fraction, no suppression. A `null` dimension (server
 * suppressed it) is HIDDEN — never shown as a zero/exact value.
 */
type Pulse = RouterOutputs["team"]["pulse"];

function statusTone(b: StatusBand): string {
  return b === "on_track"
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
    : b === "needs_attention"
      ? "bg-rose-500/10 text-rose-700 border-rose-500/20"
      : "bg-amber-500/10 text-amber-700 border-amber-500/20";
}
function blockerTone(b: BlockerBand): string {
  return b === "none"
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
    : b === "elevated"
      ? "bg-rose-500/10 text-rose-700 border-rose-500/20"
      : "bg-amber-500/10 text-amber-700 border-amber-500/20";
}
function coverageTone(b: CoverageBand): string {
  return b === "good"
    ? "bg-sky-500/10 text-sky-700 border-sky-500/20"
    : b === "limited"
      ? "bg-rose-500/10 text-rose-700 border-rose-500/20"
      : "bg-amber-500/10 text-amber-700 border-amber-500/20";
}

function Chip({ label, sub, tone }: { label: string; sub: string; tone: string }) {
  return (
    <div className={`rounded-xl border px-3 py-3 ${tone}`}>
      <div className="font-medium leading-tight">{label}</div>
      <div className="text-xs opacity-80 mt-0.5">{sub}</div>
    </div>
  );
}

export function TeamPulseCard({ pulse }: { pulse: Pulse }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const chips: Array<{ key: string; label: string; sub: string; tone: string }> = [];
  if (pulse.status)
    chips.push({ key: "status", label: isAr ? STATUS_LABEL_AR[pulse.status] : STATUS_LABEL[pulse.status], sub: isAr ? "حالة الفريق" : "Team status", tone: statusTone(pulse.status) });
  if (pulse.blockers)
    chips.push({ key: "blockers", label: isAr ? BLOCKER_LABEL_AR[pulse.blockers] : BLOCKER_LABEL[pulse.blockers], sub: isAr ? "معوقات مفتوحة" : "Open blockers", tone: blockerTone(pulse.blockers) });
  if (pulse.coverage)
    chips.push({ key: "coverage", label: isAr ? COVERAGE_LABEL_AR[pulse.coverage] : COVERAGE_LABEL[pulse.coverage], sub: isAr ? "ملخّصات مُشارَكة" : "Receipts shared", tone: coverageTone(pulse.coverage) });

  return (
    <section className="rounded-2xl border border-border bg-card/60 p-4" data-testid="team-pulse">
      <div className="text-xs font-semibold tracking-wide text-muted-foreground mb-3">
        {isAr ? "نبض الفريق" : "TEAM PULSE"}
      </div>
      {chips.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="pulse-suppressed">
          {isAr
            ? "قيد التكوين. لا يوجد نشاط مُشارَك كافٍ بعد لعرض صورة آمنة للفريق."
            : "Building. Not enough shared activity yet to show a safe team view."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {chips.map(c => (
            <Chip key={c.key} label={c.label} sub={c.sub} tone={c.tone} />
          ))}
        </div>
      )}
      <TrustNote>
        {isAr
          ? "عرض إجمالي. المقاييس الفردية تبقى خاصة."
          : "Aggregate view. Individual metrics stay private."}
      </TrustNote>
    </section>
  );
}
